import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import DetailsModal from '@/components/DetailsModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper functions
const getDeviceColor = (device) => {
  const colors = {
    'Desktop': '#6366f1',
    'Mobile': '#8b5cf6',
    'Tablet': '#a855f7'
  };
  return colors[device] || '#6366f1';
};

const getDeviceIcon = (device) => {
  const icons = {
    'Desktop': '🖥️',
    'Mobile': '📱',
    'Tablet': '💻'
  };
  return icons[device] || '📱';
};

const getBrowserIcon = (browser) => {
  const icons = {
    'Chrome': '🌐',
    'Chromium': '🌐',
    'Firefox': '🦊',
    'Safari': '🧭',
    'Microsoft Edge': '🌐',
    'Edge': '🌐',                          // legacy value (pre-backfill)
    'Opera': '🔴',
    'Opera Touch': '🔴',
    'Samsung Browser': '🌐',
    'DuckDuckGo Privacy Browser': '🦆',
    'Ecosia': '🌳',
    'Brave': '🦁',
    'Yandex Browser': '🌐',
    'Vivaldi': '🌐',
    'UC Browser': '🌐',
    'QQ Browser': '🐧',
    'Sogou Explorer': '🌐',
    'Whale': '🐳',
    'Silk': '🌐',
    'Maxthon': '🌐',
    'Mobile App': '📱',
    'IE': '🌐',
  };
  return icons[browser] || '🌐';
};

const getOSIcon = (os) => {
  const icons = {
    'Windows': '🪟',
    'Mac': '🍎',
    'macOS': '🍎',          // legacy value (pre-backfill)
    'iOS': '📱',
    'iPadOS': '📱',
    'Android': '🤖',
    'Chrome OS': '🌐',
    'GNU/Linux': '🐧',
    'Linux': '🐧',          // legacy value (pre-backfill)
    // Linux distros share the penguin marker (emoji has no distro-specific glyphs)
    'Ubuntu': '🐧',
    'Fedora': '🐧',
    'Arch Linux': '🐧',
    'openSUSE': '🐧',
    'Debian': '🐧',
    'Linux Mint': '🐧',
    'Manjaro': '🐧',
    'CentOS': '🐧',
    'Red Hat': '🐧',
  };
  return icons[os] || '💻';
};

// Reusable breakdown row component
const BreakdownRow = ({ item, count, total, icon, color, onClick, testId }) => {
  const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : '0.0';

  return (
    <div className="device-bar-row" data-testid={testId}>
      <div
        className="device-bar-bg"
        style={{
          background: `linear-gradient(90deg, ${color}15 0%, ${color}08 ${percentage}%, transparent ${percentage}%)`
        }}
      >
        <div className="device-bar-content" onClick={onClick} style={{ cursor: 'pointer' }}>
          <div className="device-info">
            <span className="device-icon-text">{icon}</span>
            <span className="device-name">{item}</span>
          </div>
          <div className="device-stats">
            <span className="device-count">{count.toLocaleString()}</span>
            <span className="device-percentage">{percentage}%</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Inline view shows top 10. If the backend reports more than this, the
// "↻ DETAILS" link appears at the bottom of the tab and the modal fetches
// the full list lazily. Keep this in sync with the .slice(0, INLINE_LIMIT)
// calls below.
const INLINE_LIMIT = 10;
// Cap on the modal fetch — far more than any real site emits in practice
// (most have 10–25 distinct browsers / OSs even over long windows).
const MODAL_FETCH_LIMIT = 200;

const DevicesInsights = ({
  analytics,
  onDeviceClick,
  onBrowserClick,
  onOsClick,
  // Props below are only needed for the Browser / OS DETAILS modal — the
  // inline view works without them, so leaving them undefined just disables
  // the "↻ DETAILS" trigger (which is also gated on totals > INLINE_LIMIT).
  websiteId,
  period,
  startDate,
  endDate,
  channel,
  location,
  device,
  os,
  browser,
  token,
  existingFilters = [],
}) => {
  const [activeTab, setActiveTab] = useState('size');

  // Modal state. modalKind ∈ {'browser','os'} drives the title, the fetch
  // payload key, and the click-to-filter behaviour inside DetailsModal.
  const [showModal, setShowModal] = useState(false);
  const [modalKind, setModalKind] = useState(null);
  const [modalRows, setModalRows] = useState([]);
  const [modalTotalVisitors, setModalTotalVisitors] = useState(0);
  const [modalLoading, setModalLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'visitors', direction: 'desc' });

  // Stable filter signature so the modal fetch only re-runs when filters
  // actually change (not on every parent re-render).
  const filtersKey = useMemo(() => (existingFilters || []).join('|'), [existingFilters]);

  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  const deviceTotal = Object.values(analytics?.devices || {}).reduce((a, b) => a + b, 0);
  const browserTotal = Object.values(analytics?.browsers || {}).reduce((a, b) => a + b, 0);
  const osTotal = Object.values(analytics?.os || {}).reduce((a, b) => a + b, 0);

  // Only surface the DETAILS link when there's something past the inline
  // limit to reveal. totals come from /tech-stats response.
  const totalBrowsers = analytics?.total_browsers ?? Object.keys(analytics?.browsers || {}).length;
  const totalOs = analytics?.total_os ?? Object.keys(analytics?.os || {}).length;
  const hasMoreBrowsers = totalBrowsers > INLINE_LIMIT;
  const hasMoreOs = totalOs > INLINE_LIMIT;

  const openDetails = (kind) => {
    setModalKind(kind);
    setShowModal(true);
  };

  const closeDetails = () => {
    setShowModal(false);
    setSearchQuery('');
    setSortConfig({ key: 'visitors', direction: 'desc' });
    setModalRows([]);
    setModalTotalVisitors(0);
  };

  // Lazy-fetch the full browser / OS list when the modal opens. Same
  // /tech-stats endpoint, just with a much higher limit so the response
  // contains every bucket. Filters are passed through verbatim so the
  // modal reflects the same scope as the inline list.
  useEffect(() => {
    const fetchModalData = async () => {
      if (!showModal || !modalKind || !websiteId) return;

      setModalLoading(true);
      try {
        let url = `${BACKEND_URL}/api/analytics/${websiteId}/tech-stats?period=${period}&limit=${MODAL_FETCH_LIMIT}&offset=0`;
        if (startDate && endDate) {
          url += `&start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(endDate)}`;
        }
        if (channel) url += `&channel=${encodeURIComponent(channel)}`;
        if (location) url += `&location=${encodeURIComponent(location)}`;
        if (device) url += `&device=${encodeURIComponent(device)}`;
        if (os) url += `&os=${encodeURIComponent(os)}`;
        if (browser) url += `&browser=${encodeURIComponent(browser)}`;
        (existingFilters || []).forEach((f) => {
          url += `&f=${encodeURIComponent(f)}`;
        });

        const response = await axios.get(url, { headers: authHeaders });
        const data = response.data || {};
        const bucket = modalKind === 'os' ? (data.os || {}) : (data.browsers || {});
        const entries = Object.entries(bucket);
        const sumVisitors = entries.reduce((acc, [, v]) => acc + (v || 0), 0);
        const rows = entries
          .map(([name, visitors]) => ({
            name,
            visitors: visitors || 0,
            percentage: sumVisitors > 0 ? ((visitors || 0) / sumVisitors) * 100 : 0,
          }))
          .sort((a, b) => b.visitors - a.visitors);

        setModalRows(rows);
        setModalTotalVisitors(sumVisitors);
      } catch (err) {
        console.error('Error fetching device details:', err);
        setModalRows([]);
        setModalTotalVisitors(0);
      } finally {
        setModalLoading(false);
      }
    };

    fetchModalData();
  }, [showModal, modalKind, websiteId, period, startDate, endDate, channel, location, device, os, browser, token, filtersKey]);

  // Filter (by search) + sort the in-memory list for the modal.
  const filteredModalRows = modalRows.filter((r) => {
    if (!searchQuery.trim()) return true;
    return r.name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const sortedModalRows = [...filteredModalRows].sort((a, b) => {
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    if (sortConfig.key === 'name') {
      return a.name.localeCompare(b.name) * dir;
    }
    const aVal = a[sortConfig.key] ?? 0;
    const bVal = b[sortConfig.key] ?? 0;
    return (aVal - bVal) * dir;
  });

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc',
    }));
  };

  // Modal title varies by kind.
  const modalTitle = modalKind === 'os' ? 'Operating Systems — All Results' : 'Browsers — All Results';

  return (
    <>
      <div className="data-table-container">
        <div className="table-header">
          <h3 className="table-title">Devices</h3>
          <div className="table-tabs">
            <button
              className={`table-tab ${activeTab === 'size' ? 'active' : ''}`}
              onClick={() => setActiveTab('size')}
              data-testid="tab-size"
            >
              Size
            </button>
            <button
              className={`table-tab ${activeTab === 'browser' ? 'active' : ''}`}
              onClick={() => setActiveTab('browser')}
              data-testid="tab-browser"
            >
              Browser
            </button>
            <button
              className={`table-tab ${activeTab === 'os' ? 'active' : ''}`}
              onClick={() => setActiveTab('os')}
              data-testid="tab-os"
            >
              OS
            </button>
          </div>
        </div>

        <div className="table-content">
          {/* Size Tab */}
          {activeTab === 'size' && (
            <div className="device-breakdown">
              {Object.entries(analytics?.devices || {})
                .sort((a, b) => b[1] - a[1])
                .map(([d, count], idx) => (
                  <BreakdownRow
                    key={idx}
                    item={d}
                    count={count}
                    total={deviceTotal}
                    icon={getDeviceIcon(d)}
                    color={getDeviceColor(d)}
                    onClick={() => onDeviceClick(d)}
                    testId={`device-${d}`}
                  />
                ))}
            </div>
          )}

          {/* Browser Tab */}
          {activeTab === 'browser' && (
            <div className="device-breakdown">
              {Object.entries(analytics?.browsers || {})
                .sort((a, b) => b[1] - a[1])
                .slice(0, INLINE_LIMIT)
                .map(([b, count], idx) => (
                  <BreakdownRow
                    key={idx}
                    item={b}
                    count={count}
                    total={browserTotal}
                    icon={getBrowserIcon(b)}
                    color="#6366f1"
                    onClick={() => onBrowserClick(b)}
                    testId={`browser-${b}`}
                  />
                ))}
            </div>
          )}

          {/* OS Tab */}
          {activeTab === 'os' && (
            <div className="device-breakdown">
              {Object.entries(analytics?.os || {})
                .sort((a, b) => b[1] - a[1])
                .slice(0, INLINE_LIMIT)
                .map(([o, count], idx) => (
                  <BreakdownRow
                    key={idx}
                    item={o}
                    count={count}
                    total={osTotal}
                    icon={getOSIcon(o)}
                    color="#10b981"
                    onClick={() => onOsClick(o)}
                    testId={`os-${o}`}
                  />
                ))}
            </div>
          )}
        </div>

        {/* DETAILS trigger — only shown on Browser / OS tabs when the
            backend reports more entries than the inline view can fit. */}
        {activeTab === 'browser' && hasMoreBrowsers && (
          <button
            className="table-details"
            onClick={() => openDetails('browser')}
            data-testid="devices-details-browser"
          >
            ↻ DETAILS
          </button>
        )}
        {activeTab === 'os' && hasMoreOs && (
          <button
            className="table-details"
            onClick={() => openDetails('os')}
            data-testid="devices-details-os"
          >
            ↻ DETAILS
          </button>
        )}
      </div>

      {/* Shared DetailsModal — `devices` type with activeTab driving the
          column label and click-to-filter behaviour. */}
      <DetailsModal
        isOpen={showModal}
        onClose={closeDetails}
        title={modalTitle}
        type="devices"
        data={sortedModalRows}
        loading={modalLoading}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        sortConfig={sortConfig}
        onSort={handleSort}
        activeTab={modalKind}
        websiteId={websiteId}
        period={period}
        startDate={startDate}
        endDate={endDate}
        channel={channel}
        location={location}
        device={device}
        os={os}
        browser={browser}
        totalCount={modalRows.length}
      />
    </>
  );
};

export default DevicesInsights;
